import { resolveLangValue } from "@/lib/utils/resolve-lang";
import type { WalletRelevanceMode } from "@/types/DocumentTemplate";
import { normalizeRelevanceMode, parseRelevanceInstant } from "@/lib/utils/wallet-relevance";
export type PassType =
  | "eventTicket"
  | "boardingPass"
  | "coupon"
  | "generic"
  | "storeCard";

export type TransitType = "air" | "train" | "boat" | "bus" | "generic";

export type FieldGroupKey =
  | "header"
  | "primary"
  | "secondary"
  | "auxiliary"
  | "back";

export type ImageSlot = "logo" | "strip" | "thumbnail" | "icon";

export type PassTypeSchema = {
  fieldGroups: { key: FieldGroupKey; maxCount: number }[];
  imageSlots: ImageSlot[];
  extraFields: "transitType"[];
};

export const PASS_TYPES: PassType[] = [
  "eventTicket",
  "boardingPass",
  "coupon",
  "generic",
  "storeCard",
];

export const TRANSIT_TYPES: TransitType[] = [
  "air",
  "train",
  "boat",
  "bus",
  "generic",
];

export const PASS_TYPE_SCHEMAS: Record<PassType, PassTypeSchema> = {
  eventTicket: {
    fieldGroups: [
      { key: "header", maxCount: 3 },
      { key: "primary", maxCount: 1 },
      { key: "secondary", maxCount: 4 },
      { key: "auxiliary", maxCount: 4 },
      { key: "back", maxCount: Infinity },
    ],
    imageSlots: ["logo", "strip", "icon"],
    extraFields: [],
  },
  boardingPass: {
    fieldGroups: [
      { key: "header", maxCount: 3 },
      { key: "primary", maxCount: 2 },
      { key: "secondary", maxCount: 4 },
      { key: "auxiliary", maxCount: 5 },
      { key: "back", maxCount: Infinity },
    ],
    imageSlots: ["logo", "icon"],
    extraFields: ["transitType"],
  },
  coupon: {
    fieldGroups: [
      { key: "header", maxCount: 3 },
      { key: "primary", maxCount: 1 },
      { key: "secondary", maxCount: 4 },
      { key: "auxiliary", maxCount: 4 },
      { key: "back", maxCount: Infinity },
    ],
    imageSlots: ["logo", "strip", "icon"],
    extraFields: [],
  },
  generic: {
    fieldGroups: [
      { key: "header", maxCount: 3 },
      { key: "primary", maxCount: 1 },
      { key: "secondary", maxCount: 4 },
      { key: "auxiliary", maxCount: 4 },
      { key: "back", maxCount: Infinity },
    ],
    imageSlots: ["logo", "thumbnail", "icon"],
    extraFields: [],
  },
  storeCard: {
    fieldGroups: [
      { key: "header", maxCount: 3 },
      { key: "primary", maxCount: 1 },
      { key: "secondary", maxCount: 4 },
      { key: "auxiliary", maxCount: 4 },
      { key: "back", maxCount: Infinity },
    ],
    imageSlots: ["logo", "strip", "thumbnail", "icon"],
    extraFields: [],
  },
};

export function getPassType(config: { passType?: string }): PassType {
  // Trimmed, because `PassJsonBuilder`'s own style validation trims. Without it
  // a stored `" coupon "` — reachable through the REST lane, which has no
  // write-side validation — ships as a COUPON while the builder draws, caps and
  // image-slots an eventTicket. Two different passes from one config, under a
  // comment on the backend claiming the two fall back the same way.
  const t = config.passType?.trim() as PassType;
  return PASS_TYPES.includes(t) ? t : "eventTicket";
}

export function getSchema(config: { passType?: string }): PassTypeSchema {
  return PASS_TYPE_SCHEMAS[getPassType(config)];
}

export function getFieldGroupMax(
  schema: PassTypeSchema,
  key: FieldGroupKey,
): number {
  return schema.fieldGroups.find((g) => g.key === key)?.maxCount ?? Infinity;
}

/**
 * Pass types where Apple caps secondary and auxiliary fields TOGETHER rather
 * than separately — but only when the front barcode is square:
 *
 *   "Coupons, store cards, and generic passes with a square barcode can have a
 *    total of up to four secondary and auxiliary fields, combined."
 *
 * Event tickets and boarding passes are explicitly outside that sentence.
 */
const COMBINED_CAP_TYPES: ReadonlySet<PassType> = new Set([
  "coupon",
  "storeCard",
  "generic",
]);
export const COMBINED_CAP = 4;

/** Barcode formats Apple draws as a square (the shape that triggers the cap). */
const SQUARE_BARCODES: ReadonlySet<string> = new Set(["qr", "aztec"]);

/**
 * How many of the configured secondary/auxiliary fields the DEVICE will
 * actually draw.
 *
 * A per-group `maxCount` cannot express a combined limit, so on a coupon with a
 * QR code the builder used to promise eight rows where Apple draws four — and
 * the hidden-data banner counted per group, so it stayed silent about it.
 * Secondary fills first because that is the order Apple lays them out in.
 */
export function getVisibleFieldCounts(
  config: { passType?: string; barcodeType?: string },
  configured: { secondary: number; auxiliary: number },
): { secondary: number; auxiliary: number } {
  const schema = getSchema(config);
  const secondaryMax = getFieldGroupMax(schema, "secondary");
  const auxiliaryMax = getFieldGroupMax(schema, "auxiliary");

  let secondary = Math.min(configured.secondary, secondaryMax);
  let auxiliary = Math.min(configured.auxiliary, auxiliaryMax);

  const squareBarcode = hasSquareBarcode(config);
  if (COMBINED_CAP_TYPES.has(getPassType(config)) && squareBarcode) {
    secondary = Math.min(secondary, COMBINED_CAP);
    auxiliary = Math.min(auxiliary, Math.max(0, COMBINED_CAP - secondary));
  }
  return { secondary, auxiliary };
}

/**
 * How many fields the operator may still ADD to a group.
 *
 * <p>Not the same question as {@link getVisibleFieldCounts}, and confusing the
 * two cost the builder its core authoring action. That function answers what
 * the DEVICE draws — `min(configured, cap)`, which by construction never
 * exceeds what already exists. Used as an editor limit it makes
 * `fields.length >= maxCount` permanently true, so no secondary or auxiliary
 * field could be added to any pass type, from an empty group, on either
 * authoring surface.
 *
 * <p>This is a CAPACITY: the group's own maximum, reduced by whatever the other
 * group has already spent out of a shared budget, and never reported below what
 * the group already holds — an older config carrying more than the cap allows
 * must not read as "delete these".
 */
export function getFieldGroupBudget(
  config: { passType?: string; barcodeType?: string },
  group: FieldGroupKey,
  configured: { secondary: number; auxiliary: number },
): number {
  const schemaMax = getFieldGroupMax(getSchema(config), group);
  if (group !== "secondary" && group !== "auxiliary") return schemaMax;

  const own = group === "secondary" ? configured.secondary : configured.auxiliary;
  const other = group === "secondary" ? configured.auxiliary : configured.secondary;
  const squareBarcode = hasSquareBarcode(config);
  if (!COMBINED_CAP_TYPES.has(getPassType(config)) || !squareBarcode) {
    return Math.max(schemaMax, own);
  }
  return Math.max(Math.min(schemaMax, Math.max(0, COMBINED_CAP - other)), own);
}

/**
 * Why a wallet config can't yet be saved/downloaded. `null` = valid. The reason
 * string doubles as the i18n key suffix (`downloadDisabled_<reason>`), so the
 * editor and the wizard share ONE source of truth (they used to duplicate these
 * checks and drift). Order is intentional: fix identity first, then the icon
 * (Apple requires `icon.png` in EVERY .pkpass — a wallet without one silently
 * fails to render at send time), then boarding-pass transit.
 */
export type WalletBlockReason =
  | "missingMetadata"
  | "missingIcon"
  | "missingTransitType"
  // A custom lock-screen window whose bounds contradict each other (end not
  // after start). NOT an incomplete one — see `getRelevanceWindowIssue`.
  | "relevanceWindowInvalid"
  // …or carries a bound the renderer cannot read. Its own reason, because its
  // own sentence: "the end has to be after the start" is a lie for a value
  // nobody can read, and that sentence is what 53 locales say for the other.
  | "relevanceWindowUnreadable"
  // The pass is rendered FOR someone. In the event-template builder there is no
  // event and no guest list, so there is nothing to resolve {{vars}} against —
  // the download would only produce a save-error toast.
  | "missingGuest";

/** Minimal shape needed for validation (avoids a hard WalletConfig import). */
type WalletValidatable = {
  passType?: string;
  /** Whether a guest is available to render the pass for. */
  hasGuest?: boolean;
  organizationName?: Record<string, string>;
  description?: Record<string, string>;
  iconUrl?: string;
  transitType?: string;
  relevanceMode?: WalletRelevanceMode | string;
  relevantStart?: string;
  relevantEnd?: string;
};

export {
  RELEVANCE_MODES,
  RELEVANCE_INSTANT,
  parseRelevanceInstant,
  toRelevanceIso,
  normalizeRelevanceMode,
  withoutRelevanceOverride,
} from "@/lib/utils/wallet-relevance";

/**
 * What is wrong with the custom lock-screen window, if anything.
 *
 * - `incomplete`: mode is custom and a bound is missing. Not a refusal — the
 *   renderer falls back to the event's window — but the panel says so, because
 *   an operator who picked "Custom window" and sees nothing happen on the phone
 *   would otherwise learn it from a guest. Template mode and an event without
 *   a start have nothing to seed the bounds from; blocking here would grey out
 *   Save until the operator picked another mode.
 * - `unreadable`: a bound is set but is not an instant the renderer reads
 *   (REST-written prose, a zoneless string). Refused before it is saved, and
 *   named as what it is — "end has to be after start" would be a lie here.
 * - `invalid`: both bounds readable and the end not after the start. The
 *   renderer would also fall back, with a note in the send report — but this
 *   is a config that cannot mean what the operator typed, so it is refused.
 *
 * Reads the bounds through `RELEVANCE_INSTANT`, the same shape the renderer's
 * `parseStrictInstant` accepts, so a window the builder lets the operator save
 * is one the renderer honours — the preview lane has no note channel to say
 * otherwise.
 */
export function getRelevanceWindowIssue(
  config: Pick<WalletValidatable, "relevanceMode" | "relevantStart" | "relevantEnd">,
): "incomplete" | "unreadable" | "invalid" | null {
  if (normalizeRelevanceMode(config.relevanceMode) !== "custom") return null;
  const startRaw = config.relevantStart?.trim() ?? "";
  const endRaw = config.relevantEnd?.trim() ?? "";
  if (!startRaw || !endRaw) {
    // A lone unreadable bound is still unreadable, not merely incomplete.
    if ((startRaw && !parseRelevanceInstant(startRaw)) || (endRaw && !parseRelevanceInstant(endRaw))) {
      return "unreadable";
    }
    return "incomplete";
  }
  const start = parseRelevanceInstant(startRaw);
  const end = parseRelevanceInstant(endRaw);
  if (!start || !end) return "unreadable";
  return end.getTime() > start.getTime() ? null : "invalid";
}

export function getWalletBlockReason(
  config: WalletValidatable,
  lang: string,
  primaryLang: string,
): WalletBlockReason | null {
  // RESOLVED, not indexed. `config.organizationName?.[lang]` asks whether the
  // text exists in the tab the operator happens to have open; the renderer asks
  // `resolve(..., lang, primaryLang, ...)`, which falls back through the region
  // variant, the primary language and finally any language that has text.
  //
  // So a template whose metadata was written in de-DE, opened on the English
  // tab, reported "missingMetadata" and BLOCKED SAVING — for a pass that would
  // have rendered perfectly. A validator stricter than the renderer it validates
  // for does not protect anyone; it just refuses correct work.
  //
  // `resolveLangValue` rather than `resolveLangKeyFromMap`: the two differ only
  // in which entry they pick as a last resort, and this is a presence question —
  // "is there any text at all" has the same answer either way.
  const org = resolveLangValue(config.organizationName, lang, primaryLang, "").trim();
  const desc = resolveLangValue(config.description, lang, primaryLang, "").trim();
  if (!org || !desc) return "missingMetadata";
  // icon.png is mandatory for a valid .pkpass on ALL pass styles.
  if (!config.iconUrl?.trim()) return "missingIcon";
  if (getPassType(config) === "boardingPass" && !config.transitType) {
    return "missingTransitType";
  }
  const relevance = getRelevanceWindowIssue(config);
  if (relevance === "unreadable") return "relevanceWindowUnreadable";
  if (relevance === "invalid") return "relevanceWindowInvalid";
  // Last: the config itself is fine, only the context is missing.
  if (config.hasGuest === false) return "missingGuest";
  return null;
}

/**
 * The strip slot's pixel size, which is NOT one number.
 *
 * Apple gives an event ticket a 375x98pt band and a coupon or store card a
 * taller 375x144pt one. The builder's hint said "375x123px" — a third figure
 * matching neither, and stated at 1x, which invites an upload too small to
 * fill the slot. These are the 3x sizes the renderer scales down to; it never
 * scales up, so 3x is what an organiser should hand it.
 *
 * Lives here rather than in the settings panel because it is a fact about the
 * pass type, like every other entry in this file.
 */
/**
 * The pixel size the RENDERER scales each image slot to, as the operator should
 * be told it.
 *
 * These are `WalletImageConverter`'s `Target`s, character for character —
 * `ICON(87,87)`, `LOGO(480,150)`, `THUMBNAIL(270,270)`. The hints used to say
 * 58x58, 160x50 and 90x90: Apple's @1x point sizes, which is a reasonable thing
 * to have read off a spec sheet and the wrong thing to tell somebody preparing a
 * file, because `scaleToFit` NEVER UPSCALES (`if (scale >= 1.0) return` the
 * source untouched). An operator who followed the logo hint exactly shipped a
 * 160x50 logo onto a @3x display and saw it soft, with the builder telling them
 * they had done it right.
 *
 * Kept beside `getStripPixelSize`, whose numbers were already the converter's,
 * so all four slots now answer from one place — and pinned against the Java
 * targets by `walletImageTargets.test.ts`.
 */
export const WALLET_IMAGE_TARGET_PX = {
  icon: "87 x 87 px",
  logo: "480 x 150 px",
  thumbnail: "270 x 270 px",
} as const;

/**
 * The three slots the cockpit lets the operator CROP. The logo is not one of
 * them: the converter fits a logo (`Target LOGO … false`, see
 * `walletImageTargets.test.ts`) and never trims it, so there is no rectangle to
 * choose.
 */
export type WalletCropSlot = "strip" | "icon" | "thumbnail";

/**
 * The narrowest crop the cockpit accepts, in SOURCE pixels — Apple's @2x size
 * for each slot. Below this the renderer would have to upscale for every
 * device sold since 2014, and `scaleToFit` refuses to (it returns the source
 * untouched), so the pass would ship a soft image. The server's own floor is
 * @1x (375 / 29 / 90); the cockpit gates a density higher on purpose, because
 * the operator can still change the picture and the renderer cannot.
 *
 * Pinned to `WALLET_IMAGE_RECOMMENDED_PX` by `walletImageTargets.test.ts`
 * (`MIN = round(RECOMMENDED · 2/3)`), so the two cannot drift.
 */
export const WALLET_IMAGE_MIN_PX = { icon: 58, thumbnail: 180, strip: 750 } as const;

/**
 * The crop width at which every density in the bundle is sharp: the @3x
 * target the converter scales to, i.e. the width in `WALLET_IMAGE_TARGET_PX`
 * and `getStripPixelSize` as a number. A crop between MIN and this ships a
 * sharp @2x and a soft @3x, which the dialog says out loud ("soft on Pro
 * devices") rather than refusing.
 */
export const WALLET_IMAGE_RECOMMENDED_PX = { icon: 87, thumbnail: 270, strip: 1125 } as const;

/**
 * The aspect ratio (width / height) a slot's crop must have. The strip's
 * depends on the pass type exactly as `getStripAspect` says — this is that
 * string as a number, not a second statement of the rule — and the icon and
 * thumbnail are square.
 */
export function getSlotAspectRatio(
  slot: WalletCropSlot,
  config: { passType?: string },
): number {
  if (slot !== "strip") return 1;
  const [w = 375, h = 98] = getStripAspect(config).split(" / ").map(Number);
  return w / h;
}

/**
 * Whether the configured barcode is one PassKit draws as a square.
 *
 * The rule behind the combined field cap, extracted from the two identical
 * inline copies that used to state it. Nothing else consumes it: an earlier
 * draft of this docstring said the strip slot needed the same answer, and that
 * consumer was removed again with the barcode axis (see `getStripAspect`) —
 * the extraction outlived its second caller.
 *
 * Known divergence, pre-existing and preserved by the extraction rather than
 * introduced by it: `PassJsonBuilder.barcodeFormat` trims, and maps a blank or
 * unrecognised type to QR. This does neither — `?? "qr"` does not catch `""`,
 * and an unknown word is not in the set. It decides only how many fields the
 * preview COUNTS as drawn, never a byte of the pass, so the two have never had
 * to agree; a future caller that touches the pass would have to fix that.
 */
export function hasSquareBarcode(config: { barcodeType?: string }): boolean {
  return SQUARE_BARCODES.has((config.barcodeType ?? "qr").toLowerCase());
}

export function getStripPixelSize(config: { passType?: string }): string {
  const type = getPassType(config);
  return type === "coupon" || type === "storeCard"
    ? "1125 x 432 px"
    : "1125 x 294 px";
}

/**
 * What the builder's always-visible caption under an image slot states
 * (W1-19): the target in pixels, the aspect ratio and whether the renderer
 * FITS or CROPS the picture. Parsed off `WALLET_IMAGE_TARGET_PX` and
 * `getStripPixelSize` — the converter's own numbers, pinned against the Java
 * by `walletImageTargets.test.ts` — so this is no second statement of them.
 * The logo is the one fitted slot (`Target LOGO … false`, `scaleToFit`
 * letterboxes it); icon, thumbnail and strip are cropped to the centre.
 * `ratio` is "w:1" to two decimals, read off `getSlotAspectRatio` for the
 * strip (so the coupon/storeCard band and the ticket band answer
 * differently) and off the target itself for the rest. The number is
 * formatted for `locale` (the cockpit's UI locale): the caption key is a
 * placeholder-only format string, so "3.83" in a German cockpit could not be
 * repaired by the translation — it has to arrive as "3,83" (audit r1).
 */
export function getWalletImageSpec(
  slot: ImageSlot,
  config: { passType?: string },
  locale = "en",
): { w: number; h: number; ratio: string; mode: "fit" | "crop" } {
  const px = slot === "strip" ? getStripPixelSize(config) : WALLET_IMAGE_TARGET_PX[slot];
  const [w = 0, h = 0] = px.split(" x ").map((n) => Number.parseInt(n, 10));
  const ratio = slot === "strip" ? getSlotAspectRatio("strip", config) : w / h;
  const ratioText = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(ratio);
  return {
    w,
    h,
    ratio: `${ratioText}:1`,
    mode: slot === "logo" ? "fit" : "crop",
  };
}

/**
 * The strip slot's aspect ratio as a CSS `aspect-ratio` value — the same fact
 * as {@link getStripPixelSize}, in the form a preview box needs.
 *
 * The overlay used to hard-code a 100px height for every pass type, which is
 * neither: at the preview's 320px width an event ticket's band is ~84px and a
 * coupon's ~123px. A preview that draws the wrong band height misrepresents how
 * much of the artwork survives the crop, which is the one thing the strip slot
 * is previewed for.
 *
 * Corroborated on a device: a real event ticket draws its band at 99.2pt on a
 * 375pt face, i.e. 3.78:1 against the 3.83:1 this returns. Note what that does
 * and does not establish — the renderer had already cropped that pass's strip
 * to 1125x294, so it shows the device drawing the artwork FULL WIDTH without
 * letterboxing, not where the slot's ceiling sits. The ceiling is the archived
 * Apple guide's, via main-backend commit d595362e.
 *
 * A BARCODE axis was tried here and taken out again. Apple's current HIG gives
 * no strip size for an event ticket at all and the archived guide names only
 * 375x98pt (see main-backend commit d595362e, which settled it on a device);
 * the 375x123pt that a square-versus-linear split would need appears in no
 * source at all. (Two committed comments here and in
 * `walletPassTypeSchemas.limits.test.ts` do reject a "375x123" — but that one
 * is 123 PIXELS at 1x, the old one-size-fits-all upload hint, which is a
 * different proposition. Three quantities in this file share those digits; the
 * "~123px" a few lines up is a coupon's band in preview pixels and is CORRECT.
 * The case against the axis rests on the absence of a source, not on borrowing
 * those comments' authority.) It also could
 * not be measured the way 98 was: the shot it would have come from is of a pass
 * whose strip the renderer had already cropped to 1125x294, so it shows the
 * device drawing the artwork full width, not where the slot's ceiling is. One
 * unmeasured number, contradicted in-repo, for a case no preset and no default
 * reaches, is not worth the third statement of the rule it costs.
 */
export function getStripAspect(config: { passType?: string }): string {
  const type = getPassType(config);
  return type === "coupon" || type === "storeCard" ? "375 / 144" : "375 / 98";
}

/**
 * The four values a field can hold that the renderer will hand to the device
 * as an instant — and nothing else.
 *
 * Apple formats a date itself only from ISO-8601, so the value has to BE the
 * date. "{{eventDate}} um {{eventTime}}" is prose and there is no instant
 * behind it. The pass builder applies exactly this rule; it lives here so the
 * editor and the preview cannot drift from it, which is how the preview came
 * to draw a formatted date for a field that ships text.
 */
const DATE_TOKENS = ["{{eventDate}}", "{{eventTime}}", "{{eventEndDate}}", "{{eventEndTime}}"];

export function isSingleDateToken(value: string | undefined): boolean {
  return value !== undefined && DATE_TOKENS.includes(value.trim());
}

/** Whether a chosen style actually puts something on the pass. */
export function dateStyleDrawsSomething(style: string | undefined | null): boolean {
  // `!= null` on purpose, covering null AND undefined. With `!== undefined`
  // this returned TRUE for null — strictly weaker than the `style || …`
  // truthiness it replaced, which is a worse version of the very bug it fixes:
  // the editor would show its "this format will not be applied" line forever on
  // a field with nothing configured. Not reachable through the REST lane today
  // (Yasson omits nulls), but the type said `string | undefined` while the
  // runtime value could be null, and that gap is the whole finding.
  return style != null && style !== "" && style !== "none";
}
